import * as wjcCore from '@mescius/wijmo';
import * as wjcGrid from '@mescius/wijmo.grid';
import {Component, OnInit, ViewChild} from '@angular/core';
import {ToasterService} from 'angular2-toaster';
import {DataService} from '@cosbeni/database';
import {ActivePromotionConfig} from '../config/active-promotion.config';
import {KobeacService} from './kobeac.service';
import {KobeacItemGridBase} from './item-grid.base';
import {TranslocoService} from '@jsverse/transloco';

/** kobe-ac アイテム管理 / 一括入力 (旧 /auction/input) */
@Component({
    standalone: false,
    selector: 'kobeac-input',
    templateUrl: 'input.component.html',
})
export class KobeacInputComponent extends KobeacItemGridBase implements OnInit {
    @ViewChild('itemGrid') itemGrid: wjcGrid.FlexGrid;
    @ViewChild('inputGrid') inputGrid: wjcGrid.FlexGrid;
    /** 入力タブのグリッドに表示する行数 */
    static readonly INPUT_ROWS = 10;

    input: wjcCore.CollectionView = new wjcCore.CollectionView([]);
    supplier: any = null;
    commision: any = null;
    max = 1;
    readonly = true;
    suppliers: any[] = [];
    bidders: any[] = [];
    configurations: any[] = [];

    constructor(dataService: DataService, kobeac: KobeacService, private toasterService: ToasterService,
                private transloco: TranslocoService) {
        super(dataService, kobeac);
        this.items.itemsSourceFunction = (rows: any[]) => {
            this.kobeac.resolveCustomers(rows);
            // 番号は選択中オークションの一覧の最大値 + 1。オークションを切り替えたら
            // 前のオークションの最大値を持ち越さないよう読込のたびに 1 から数え直す (旧 kobe-ac.app と同じ)。
            this.max = 1;
            for (const item of rows) {
                if (wjcCore.asString(item['@class']) == 'AC_Item' && wjcCore.isInt(item.ID) && item.ID >= this.max) {
                    this.max = item.ID + 1;
                }
            }
            return rows;
        };
    }

    async ngOnInit() {
        await this.kobeac.ensureLoaded();
        this.initMaps();
        this.configurations = this.kobeac.configurations;
        this.suppliers = this.kobeac.customers
            .filter((c) => c['売主'] && !(c['無効'] === true))
            .map((c) => ({id: c, text: c.ID + ' ' + c['名称']}));
        this.bidders = this.kobeac.customers
            .filter((c) => c['買主'] && !(c['無効'] === true))
            .map((c) => ({id: c, text: c.ID + ' ' + c['名称']}));
        this.items.filterOnServer = true;
    }

    /**
     * 行見出し (番号 + 入力済みチェック) は 4 桁でも収まるよう既定より広げる。
     * Tab / Shift+Tab で隣のセルへ移動し、行末で次の行へ折り返す (旧 kobe-ac.app の Wijmo と同じ操作)。
     * 現行 Wijmo の既定 (None) ではグリッド外へフォーカスが抜けてしまう。
     */
    onInputGridInitialized(grid: wjcGrid.FlexGrid): void {
        grid.rowHeaders.columns.defaultSize = 80;
        grid.keyActionTab = wjcGrid.KeyAction.Cycle;
        // 入力欄の高さは約 10 行分 (列見出し + 10 行 + 横スクロールバー)
        const header = grid.columnHeaders.rows.length * grid.columnHeaders.rows.defaultSize;
        grid.hostElement.style.height = (header + grid.rows.defaultSize * KobeacInputComponent.INPUT_ROWS + 20) + 'px';
    }

    suppliersSource = (query: string, max: number, callback: Function) => {
        const q = (query || '').toLowerCase();
        const results = this.suppliers.filter((b) => b.text.toLowerCase().indexOf(q) >= 0);
        if (results.length > 0) callback(results);
    };

    biddersSource = (query: string, max: number, callback: Function) => {
        const q = (query || '').toLowerCase();
        const results = this.bidders.filter((b) => b.text.toLowerCase().indexOf(q) >= 0);
        if (results.length > 0) callback(results);
    };

    beginningEdit = (s: wjcGrid.FlexGrid, e: wjcGrid.CellRangeEventArgs) => {
        const item = s.rows[e.row].dataItem;
        if (item != null) {
            if (item['個数'] === undefined) item['個数'] = 1;
            if (item['単価'] === undefined) item['単価'] = 0;
        }
    };

    cellEditEnded = (s: wjcGrid.FlexGrid, e: wjcGrid.CellRangeEventArgs) => {
        if (s.columns[e.col].binding == '商品名') s.invalidate();
    };

    validate(name: string, item?: any): boolean {
        if (name == 'supplier') return wjcCore.isObject(this.supplier);
        if (name == 'commision') return wjcCore.isNumber(this.commision);
        if (name == 'item') {
            if (!wjcCore.isObject(item)) return false;
            if (!wjcCore.isNumber(item['単価'])) return false;
            if (!wjcCore.isNumber(item['個数'])) return false;
            if (!wjcCore.isObject(item['買主'])) return false;
            if (!wjcCore.isString(item['商品名'])) return false;
            return true;
        }
        return false;
    }

    canSave(): boolean {
        if (!wjcCore.isObject(this.current)) return false;
        if (!wjcCore.isObject(this.supplier)) return false;
        if (!wjcCore.isNumber(this.commision)) return false;
        for (const item of this.input.items) {
            if (!this.validate('item', item)) return false;
        }
        return this.input.items.length > 0;
    }

    /** 保存中は true (保存ボタンの二度押し防止) */
    saving = false;

    /**
     * 他の端末が同じオークションに入力している場合があるため、保存直前にサーバの最大番号を
     * 取り直してから採番する (一覧を読み込んだ時点の max のままだと番号が重複する)。
     */
    save(): void {
        if (this.saving) return;
        this.saving = true;
        const sql = 'SELECT max(ID) AS maxId FROM AC_Item WHERE `オークション` = ' + this.current['@rid'];
        this.dataService.query({sql: sql, count: sql}, ActivePromotionConfig.SERVER_URL).subscribe({
            next: (response: any) => {
                const maxId = wjcCore.isArray(response.results) && response.results.length ? response.results[0].maxId : null;
                if (wjcCore.isInt(maxId) && maxId + 1 > this.max) {
                    this.max = maxId + 1;
                }
                this.saveRows();
            },
            error: () => {
                this.saving = false;
                this.toasterService.pop('error', this.transloco.translate('common.save'), this.transloco.translate('common.failed'));
            },
        });
    }

    private saveRows(): void {
        const rows = this.input.items;
        let index = 0;
        for (const item of rows) {
            item['@class'] = 'AC_Item';
            item['@rid'] = '#-1:-1';
            item.ID = this.max + index;
            index = index + 1;
            item['オークション'] = this.current;
            item['売主'] = this.supplier;
            item['売歩'] = this.commision;
            item['買歩'] = this.configurations.length ? this.configurations[0]['買歩設定'] : null;
        }
        this.dataService.update(rows, ActivePromotionConfig.SERVER_URL).subscribe({
            next: () => {
                this.saving = false;
                this.toasterService.pop('info', this.transloco.translate('common.save'), this.transloco.translate('kobeac.saved_passive'));
                this.items.cancelChanges();
                this.reset();
            },
            error: () => {
                this.saving = false;
                this.toasterService.pop('error', this.transloco.translate('common.save'), this.transloco.translate('common.failed'));
            },
        });
    }

    reset(): void {
        this.input = new wjcCore.CollectionView([]);
    }

    /**
     * 一覧を最新の内容で読み直す (他の端末で入力された明細も表示する)。
     * 一覧タブを開いたときと「更新」ボタンで呼ぶ。未保存の編集がある間は読み直さない。
     */
    refreshList(): void {
        if (!wjcCore.isObject(this.current) || this.items.hasChanges()) return;
        this.items.cancelChanges();
    }

    saveItemChanges(): void {
        this.items.saveChanges();
    }

    cancelItemChanges(): void {
        this.items.cancelChanges();
    }
}
